import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { Icon } from '@festina-life/ui/structure'
import cx from 'classnames'

import { useBaseBranchWorkspaces } from '../../hooks/useBaseBranchWorkspaces'
import { useNavigationParams } from '../../system/useNavigationParams'
import { isEqualBranch } from '../../uiModels/branches/helpers'
import { isProtectedBranchCategory } from '../../uiModels/branches/types'
import { useConciseBranch } from '../../uiModels/branches/useConciseBranch'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { Attribute } from '../attribute/Attribute'
import { useBaseBranchStatus } from '../branchesPanel/branchesTree/useBaseBranchStatus'
import { BranchNameAndCategory } from './BranchNameAndCategory'

type Props = {
  branchKey: BranchKey
  label?: Optional<string>
}

export const ConfigurationBranchStatus = ({ branchKey, label }: Props) => {
  const translate = useTranslate()
  const { branch, server } = useNavigationParams()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const conciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: true,
  })
  const baseConciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: conciseBranch?.baseBranchName,
    shouldFallbackToStandard: false,
  })
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const status = useBaseBranchStatus(branchKey)

  const isActive = branchKey.server === server && branchKey.name === branch

  const isBranchDeleted = !conciseBranch
  const isBaseBranchDeleted =
    !!conciseBranch?.baseBranchName && !baseConciseBranch

  const isInError =
    status === 'outOfSync' || isBranchDeleted || isBaseBranchDeleted

  const isUnifiedBranchWithOpenWorkspace =
    branchKey.name !== STANDARD_BRANCH &&
    branchWorkspaces.length <= 1 &&
    !!currentWorkspaceKey &&
    isEqualBranch(currentWorkspaceKey.branchKey, branchKey)

  const isTextColoredAsDraft =
    branchWorkspaces.length === 1 && !isUnifiedBranchWithOpenWorkspace

  // Out of sync indicators only make sense for feature branches since release branches will always end up being out of sync
  const shouldShowOutOfSyncDot =
    isInError && !isProtectedBranchCategory(conciseBranch?.branchCategory)

  return conciseBranch ? (
    <Attribute
      isFaded={false}
      isShowingIndicator={shouldShowOutOfSyncDot}
      indicatorLabel={translate('branchesTree.status.base.branchOutOfSync')}
      className={cx(
        isUnifiedBranchWithOpenWorkspace
          ? 'bg-draft-600 dark:bg-draft-400 text-amaze-inkBright! dark:text-amaze-ink!'
          : {
              'bg-success-300! text-amaze-ink!': isActive,
              'text-draft-600 dark:text-draft-400': isTextColoredAsDraft,
              'bg-transparent!': !isActive,
            },
      )}
    >
      <BranchNameAndCategory
        specification={conciseBranch}
        label={label}
        className={
          isTextColoredAsDraft
            ? 'text-draft-600 dark:text-draft-400'
            : undefined
        }
      />
      {branchWorkspaces.length === 1 && (
        <Icon
          name="Briefcase"
          color="unset"
          className={
            isTextColoredAsDraft
              ? 'text-draft-600! dark:text-draft-400!'
              : undefined
          }
        />
      )}
    </Attribute>
  ) : null
}
